import { useLiveQuery } from 'dexie-react-hooks'
import { BadgeCheck, Printer, Truck } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import type { Comanda, Factura } from '@/db/dexie'
import { getSetari } from '@/db/queries'
import { fmtData, fmtRON, fmtSuma } from '@/lib/format'
import { cn } from '@/lib/utils'

const nrFmt = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 1 })
const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Scoped print stylesheet. Mounted only while the invoice is on screen, so it
 * never leaks to the rest of the app. On print we hide all app chrome, unwrap
 * the shadcn dialog box, and let ONLY `#factura-tiparire` render on the page.
 */
const PRINT_CSS = `
@media print {
  @page { size: A4; margin: 14mm; }
  body * { visibility: hidden !important; }
  [data-slot="dialog-overlay"] { display: none !important; }
  [data-slot="dialog-header"],
  [data-slot="dialog-close"] { display: none !important; }
  [data-slot="dialog-content"] {
    position: static !important;
    transform: none !important;
    inset: auto !important;
    width: auto !important;
    max-width: none !important;
    max-height: none !important;
    overflow: visible !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    background: transparent !important;
  }
  #factura-tiparire,
  #factura-tiparire * {
    visibility: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  #factura-tiparire {
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    max-height: none !important;
    overflow: visible !important;
  }
  .no-print { display: none !important; }
}
`

function initiale(nume: string): string {
  return nume
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

function ParteBloc({
  titlu,
  children,
  className,
}: {
  titlu: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {titlu}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function Rand({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <span className={cn('min-w-0 text-foreground', mono && 'font-mono nums')}>{value}</span>
    </div>
  )
}

function TotalRand({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono nums text-foreground">{value}</dd>
    </div>
  )
}

/**
 * A print-ready Romanian factură fiscală. Furnizor identity + IBAN/telefon come
 * from `setari` (falling back to the snapshot stored on the invoice); the meat
 * e-Transport UIT code comes from the linked comandă. Renders the two mandatory
 * RO compliance markers (ANAF e-Factura, cod UIT) as an automatic byproduct.
 */
export function Invoice({
  factura,
  comanda,
  className,
}: {
  factura: Factura
  comanda?: Comanda
  className?: string
}) {
  const setari = useLiveQuery(getSetari, [])
  const codUIT = comanda?.codUIT
  const eFacturaTrimis = factura.eFacturaStatus === 'validat' || factura.eFacturaStatus === 'trimis'
  const eFacturaLabel =
    factura.eFacturaStatus === 'validat'
      ? 'Validat la ANAF e-Factura'
      : factura.eFacturaStatus === 'trimis'
        ? 'Trimis la ANAF e-Factura'
        : 'e-Factura în așteptare'

  const furnizorNume = setari?.companie ?? factura.furnizor.nume

  return (
    <div className={cn('space-y-4', className)}>
      <style>{PRINT_CSS}</style>

      <div className="no-print flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="size-4" /> Printează
        </Button>
      </div>

      <article
        id="factura-tiparire"
        className="rounded-lg border border-hairline bg-card p-6 text-[13px] leading-relaxed text-foreground shadow-paper sm:p-8"
      >
        {/* header */}
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-5">
          <div className="flex items-center gap-3">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-amber font-display text-lg font-semibold text-amber-ink">
              {initiale(furnizorNume)}
            </div>
            <div>
              <div className="font-display text-xl font-medium tracking-tight">Factură fiscală</div>
              <div className="text-xs text-muted-foreground">
                Document fiscal · generat electronic
              </div>
            </div>
          </div>
          <div className="rounded-md border border-hairline px-4 py-2 text-right">
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Serie
              </span>
              <span className="font-mono nums text-sm font-semibold">{factura.serie}</span>
              <span className="ml-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                Nr.
              </span>
              <span className="font-mono nums text-sm font-semibold">{factura.numar}</span>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Data emiterii:{' '}
              <span className="font-mono nums text-foreground">{fmtData(factura.dataEmitere)}</span>
            </div>
          </div>
        </header>

        {/* parties */}
        <section className="grid grid-cols-1 gap-6 py-5 sm:grid-cols-2">
          <ParteBloc titlu="Furnizor">
            <div className="font-medium text-foreground">{furnizorNume}</div>
            <Rand label="CUI" value={setari?.cui ?? factura.furnizor.cui} mono />
            <Rand label="Adresă" value={setari?.adresa ?? factura.furnizor.adresa} />
            {setari?.iban && <Rand label="IBAN" value={setari.iban} mono />}
            {setari?.telefon && <Rand label="Telefon" value={setari.telefon} mono />}
          </ParteBloc>
          <ParteBloc titlu="Client" className="sm:border-l sm:border-hairline sm:pl-6">
            <div className="font-medium text-foreground">{factura.client.nume}</div>
            <Rand label="CUI" value={factura.client.cui} mono />
            <Rand label="Adresă" value={factura.client.adresa} />
          </ParteBloc>
        </section>

        {/* line items */}
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-y border-hairline text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="w-8 py-2 pr-2 text-left font-medium">Nr.</th>
              <th className="py-2 pr-2 text-left font-medium">Denumire produs</th>
              <th className="px-2 py-2 text-center font-medium">U.M.</th>
              <th className="px-2 py-2 text-right font-medium">Cant.</th>
              <th className="px-2 py-2 text-right font-medium">Preț unitar</th>
              <th className="py-2 pl-2 text-right font-medium">Valoare</th>
            </tr>
          </thead>
          <tbody>
            {factura.linii.map((l, i) => (
              <tr key={`${l.nume}-${i}`} className="border-b border-hairline/70">
                <td className="py-2 pr-2 font-mono nums text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-2 text-foreground">{l.nume}</td>
                <td className="px-2 py-2 text-center text-muted-foreground">kg</td>
                <td className="px-2 py-2 text-right font-mono nums">
                  {nrFmt.format(l.cantitateKg)}
                </td>
                <td className="px-2 py-2 text-right font-mono nums">{fmtSuma(l.pretKg)}</td>
                <td className="py-2 pl-2 text-right font-mono nums">
                  {fmtSuma(round2(l.cantitateKg * l.pretKg))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* totals */}
        <div className="mt-5 flex justify-end">
          <dl className="w-full max-w-xs space-y-1.5 text-sm">
            <TotalRand label="Subtotal (bază)" value={fmtRON(factura.subtotal)} />
            <TotalRand
              label={`TVA ${Math.round(factura.tva * 100)}% (aliment)`}
              value={fmtRON(factura.totalTVA)}
            />
            <div className="mt-1 flex items-baseline justify-between border-t border-hairline pt-2">
              <dt className="font-display text-base font-medium">TOTAL de plată</dt>
              <dd className="font-mono nums text-lg font-semibold text-amber-ink">
                {fmtRON(factura.total)}
              </dd>
            </div>
          </dl>
        </div>

        {/* compliance markers */}
        <footer className="mt-6 border-t border-hairline pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium',
                eFacturaTrimis ? 'bg-pine-soft text-pine' : 'bg-amber-soft text-amber-ink',
              )}
            >
              <BadgeCheck className="size-3.5" />
              {eFacturaLabel}
              {eFacturaTrimis ? ' ✓' : ''}
            </span>
            {codUIT && (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-soft px-2.5 py-1 text-xs font-medium text-amber-ink">
                <Truck className="size-3.5" />
                cod UIT (e-Transport): <span className="font-mono nums">{codUIT}</span>
              </span>
            )}
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
            Ambele coduri sunt obligatorii legal și emise automat:{' '}
            <strong className="font-medium text-foreground">e-Factura</strong> — obligatorie B2B din
            iul. 2024;{' '}
            <strong className="font-medium text-foreground">e-Transport (cod UIT)</strong> —
            obligatoriu pentru transportul de carne din mar. 2024.
          </p>
        </footer>
      </article>
    </div>
  )
}
